import React, { useState, useEffect, useRef } from 'react';
import { get } from 'lodash';
import { Drawer, Table, Space, Button, Popconfirm, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import intl from 'react-intl-universal';
import { connect } from 'react-redux';
import { DATE_TIME_FORMAT } from '@/config';
import { getTimeByUtcOffset } from '@/utils/intl';
import * as CardApi from '@/api/card';

import CreateCardModal from './create';
import UpdateCardModal from './update';
import CheckCardModal from './check';
import CreateDoorCardModal from './components/CreateDoorCardModal';
import UpdateDoorCardModal from './components/UpdateDoorCardModal';
import DeleteDoorCardModal from './components/DeleteDoorCardModal';
import { CardsDrawerProps } from './interface';
import { CARD_STATE_MAP, LockVendor } from './config';
import styles from './index.module.scss';

// 此文件夹下代码逻辑解释权归汤伟伟
const CardsDrawer: React.FC<CardsDrawerProps> = ({
  enabledVendor,
  cardsDrawerVisible,
  recNo,
  roomId,
  roomNo,
  checkInTime,
  checkOutTime,
  ...restProps
}) => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [cardListLoading, setCardListLoading] = useState(false);
  const [cardList, setCardList] = useState([]);
  const [lossLoading, setLossLoading] = useState(false);
  const [lockNo, setLockNo] = useState('');

  const [createModalVisibile, setCreateModalVisibile] = useState(false);
  const [updateModalVisibile, setUpdateModalVisibile] = useState(false);
  const [checkModalVisibile, setCheckModalVisibile] = useState(false);

  // 获取锁号
  const initLockNo = async (id) => {
    try {
      const res = await CardApi.fetchLockNoByRoomId({
        roomId: id,
      });
      if (res && res.status) {
        setLockNo(res.data || '');
      } else {
        setLockNo('');
      }
    } catch (err) {
      setLockNo('');
    }
  };

  const initCardList = async () => {
    setCardListLoading(true);
    try {
      const res = await CardApi.fetchRoomCardList({
        recNo,
        checkinTime: checkInTime,
        expectedDepartureTime: checkOutTime,
      });
      setCardListLoading(false);
      if (res && res.status) {
        const list = get(res, 'data', []);
        setCardList(list);
      } else {
        setCardList([]);
      }
    } catch (e) {
      setCardListLoading(false);
      setCardList([]);
    }
  };

  const changeVisible = (visible) => {
    if (visible) {
      initCardList();
      if (roomId) initLockNo(roomId);
    } else {
      setCardList([]);
      setLockNo('');
      if (typeof restProps.closeCallBack === 'function') restProps.closeCallBack();
    }
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
  };

  const crateRoomCard = () => {
    if (!lockNo) {
      message.warning(intl.get('cpm_cards_add_lock_no_first_then_make_room_card'));
      return;
    }
    setCreateModalVisibile(true);
  };

  const checkRoomCard = () => {
    setCheckModalVisibile(true);
  };

  const updateRoomCard = () => {
    setUpdateModalVisibile(true);
  };

  const lossRoomCard = async (record) => {
    setLossLoading(true);
    try {
      const res = await CardApi.lossRoomCard({
        id: record.id,
      });
      setLossLoading(false);
      if (res && res.status) {
        message.success(intl.get('cpm_cards_report_loss_success'));
        initCardList();
      }
    } catch (e) {
      setLossLoading(false);
    }
  };

  const cardColumns: ColumnsType<any> = [
    {
      title: intl.get('app_room'),
      dataIndex: 'roomNo',
      render: (v) => v || '-',
    },
    {
      title: intl.get('device_lock_number'),
      dataIndex: 'lockNo',
      render: (v) => v || '-',
    },
    {
      title: intl.get('manage_license_field_start_label'),
      dataIndex: 'validityStartTime',
      render: (val) => getTimeByUtcOffset(val, DATE_TIME_FORMAT) || '-',
    },
    {
      title: intl.get('cpm_cards_end_validity_period'),
      dataIndex: 'validityEndTime',
      render: (val) => getTimeByUtcOffset(val, DATE_TIME_FORMAT) || '-',
    },
    {
      title: intl.get('app_status'),
      dataIndex: 'cardStatus',
      render: (status) => (CARD_STATE_MAP[status] ? CARD_STATE_MAP[status] : '-'),
    },
    {
      title: intl.get('app_table_column_action'),
      align: 'right',
      dataIndex: 'opertions',
      render: (_, record) => (
        <Popconfirm
          title={() => (
            <div style={{ width: 320 }}>
              {intl.get('cpm_cards_report_loss_tips1')}
              <strong style={{ color: 'red' }}>{intl.get('cpm_cards_report_loss_tips2')}</strong>
            </div>
          )}
          placement="topRight"
          okButtonProps={{
            loading: lossLoading,
          }}
          okText={intl.get('cpm_cards_report_loss_confirm')}
          onConfirm={() => lossRoomCard(record)}
        >
          <Button style={{ paddingRight: 0 }} type="link">
            {intl.get('cpm_cards_report_loss')}
          </Button>
        </Popconfirm>
      ),
    },
  ];

  useEffect(() => {
    setDrawerVisible(cardsDrawerVisible);
  }, [cardsDrawerVisible]);

  const createRef = useRef<any>();
  const updateRef = useRef<any>();
  const deleteRef = useRef<any>();

  return (
    <Drawer
      width="50%"
      open={drawerVisible}
      title={intl.get('cpm_cards_room_card_manage')}
      maskClosable={false}
      closable={false}
      destroyOnClose
      afterOpenChange={(visible) => changeVisible(visible)}
      extra={
        <Button
          style={{ border: 0 }}
          shape="circle"
          onClick={() => closeDrawer()}
          icon={<CloseOutlined />}
        />
      }
      footer={
        <Button type="primary" onClick={() => closeDrawer()}>
          {intl.get('app_btn_close')}
        </Button>
      }
    >
      {enabledVendor?.lockCode === LockVendor.BE_LINE ? (
        <>
          <CreateDoorCardModal
            innerRef={createRef}
            enabledVendor={enabledVendor}
            roomId={roomId}
            roomNo={roomNo}
            createCallBack={() => {
              createRef.current.closeModal();
              initCardList();
            }}
          />
          <UpdateDoorCardModal
            innerRef={updateRef}
            enabledVendor={enabledVendor}
            roomId={roomId}
            roomNo={roomNo}
            updateCallBack={() => {
              updateRef.current.closeModal();
              initCardList();
            }}
          />
          <DeleteDoorCardModal
            innerRef={deleteRef}
            enabledVendor={enabledVendor}
            roomIds={[roomId]}
            roomNo={roomNo}
            checkCallBack={() => {
              deleteRef.current.closeModal();
              initCardList();
            }}
          />
        </>
      ) : (
        <>
          <CreateCardModal
            createModalVisibile={createModalVisibile}
            roomId={roomId}
            roomNo={roomNo}
            createCallBack={() => {
              setCreateModalVisibile(false);
              initCardList();
            }}
          />
          <UpdateCardModal
            updateModalVisibile={updateModalVisibile}
            roomId={roomId}
            roomNo={roomNo}
            updateCallBack={() => {
              setUpdateModalVisibile(false);
              initCardList();
            }}
          />
          <CheckCardModal
            checkModalVisibile={checkModalVisibile}
            roomIds={[roomId]}
            roomNo={roomNo}
            checkCallBack={() => {
              setCheckModalVisibile(false);
              initCardList();
            }}
          />
        </>
      )}

      <div className={styles.CardsDrawer}>
        <p className={styles.title}>{intl.get('cpm_cards_room_card_list')}</p>
        {enabledVendor?.lockCode === LockVendor.BE_LINE ? (
          <>
            <Space size={16} className={styles.operates}>
              <Button type="primary" onClick={() => createRef.current.openModal()}>
                {intl.get('cpm_cards_add_room_card')}
              </Button>
              <Button type="primary" onClick={() => updateRef.current.openModal()}>
                {intl.get('cpm_cards_update_room_card')}
              </Button>
              <Button type="primary" onClick={() => deleteRef.current.openModal()}>
                {intl.get('cpm_cards_return_room_card')}
              </Button>
            </Space>
          </>
        ) : (
          <>
            <Space size={16} className={styles.operates}>
              <Button type="primary" onClick={() => crateRoomCard()}>
                {intl.get('cpm_cards_add_room_card')}
              </Button>
              <Button type="primary" onClick={() => updateRoomCard()}>
                {intl.get('cpm_cards_update_room_card')}
              </Button>
              <Button type="primary" onClick={() => checkRoomCard()}>
                {intl.get('cpm_cards_return_room_card')}
              </Button>
            </Space>
          </>
        )}

        <Table
          rowKey="id"
          loading={cardListLoading}
          pagination={false}
          dataSource={cardList || []}
          columns={cardColumns}
        />
      </div>
    </Drawer>
  );
};

const mapStateToProps = (state) => {
  const { roomCardEnabled, enabledVendor } = state.card;
  return { roomCardEnabled, enabledVendor };
};

export default connect(mapStateToProps, null)(CardsDrawer);
